import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { DollarSign, Car, Users, TrendingUp, Clock, AlertTriangle, Target, Flame, Plus, Handshake, ListTodo, User } from "lucide-react";
import { formatDistanceToNow, isPast, differenceInDays } from "date-fns";
import { formatCurrency, DEAL_STAGES, getDealStageConfig } from "@/types/crm";
import type { Activity, Task, Deal, Customer } from "@/types/crm";
import { useNavigate } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { AddCustomerDialog } from "@/components/admin/AddCustomerDialog";
import { CreateTaskDialog } from "@/components/admin/CreateTaskDialog";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalVehicles: 0,
    availableVehicles: 0,
    soldVehicles: 0,
    totalLeads: 0,
    totalCustomers: 0,
    hotLeads: 0,
    avgDaysOnLot: 0,
    mtdRevenue: 0,
  });
  const [deals, setDeals] = useState<Deal[]>([]);
  const [recentActivities, setRecentActivities] = useState<(Activity & { customer?: Pick<Customer, 'first_name' | 'last_name'> | null })[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<(Task & { customer?: Pick<Customer, 'first_name' | 'last_name'> | null })[]>([]);
  const [dealsByStage, setDealsByStage] = useState<Record<string, number>>({});

  useEffect(() => { 
    loadAll(); 
    const interval = setInterval(() => {
      loadAll();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAll = async () => {
    const [vehiclesRes, leadsRes, customersRes, dealsRes, activitiesRes, tasksRes] = await Promise.all([
      supabase.from("vehicles").select("id, status, created_at, sold_date, sold_price", { count: "exact" }),
      supabase.from("contact_submissions").select("id", { count: "exact", head: true }),
      supabase.from("customers").select("lead_score", { count: "exact" }),
      supabase.from("deals").select("id, stage, sale_price, front_gross, closed_at, delivery_date, updated_at, vehicle_id").order("created_at", { ascending: false }),
      supabase.from("activities").select("*, customer:customers(first_name, last_name)").order("created_at", { ascending: false }).limit(10),
      supabase.from("tasks").select("*, customer:customers(first_name, last_name)").is("completed_at", null).order("due_date", { ascending: true }).limit(10),
    ]);

    const vehicles = vehiclesRes.data || [];
    const available = vehicles.filter(v => v.status === "available");
    const sold = vehicles.filter(v => v.status === "sold");
    const customers = customersRes.data || [];
    const allDeals = (dealsRes.data as any[] || []) as Deal[];

    // Calculate avg days on lot for available vehicles
    const avgDays = available.length > 0
      ? Math.round(available.reduce((sum, v) => sum + differenceInDays(new Date(), new Date(v.created_at)), 0) / available.length)
      : 0;

    // MTD revenue — deduplicated: prefer deal data, fallback to vehicle sold_price
    // Revenue is recognized on delivery date (when customer takes possession)
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const parseLocalDate = (dateStr: string | null | undefined): Date | null => {
      if (!dateStr) return null;
      try {
        const baseStr = dateStr.split("T")[0];
        const parts = baseStr.split("-");
        if (parts.length !== 3) {
          const parsed = new Date(dateStr);
          return isNaN(parsed.getTime()) ? null : parsed;
        }
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // 0-indexed
        const day = parseInt(parts[2], 10);
        if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
        if (month < 0 || month > 11 || day < 1 || day > 31) return null;
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? null : date;
      } catch {
        return null;
      }
    };

    // Collect vehicle IDs that have a delivered/funded deal this month
    const mtdDeliveredDeals = allDeals.filter(d => {
      if (d.stage !== 'delivered' && d.stage !== 'funded') return false;
      const dateStr = (d as any).delivery_date || d.closed_at;
      if (!dateStr) return false;
      const dDate = parseLocalDate(dateStr);
      return dDate ? (dDate.getMonth() === currentMonth && dDate.getFullYear() === currentYear) : false;
    });
    const dealVehicleIds = new Set(mtdDeliveredDeals.map(d => d.vehicle_id).filter(Boolean));
    const mtdRevenueDeals = mtdDeliveredDeals.reduce((sum, d) => sum + (d.sale_price || 0), 0);

    // Only count vehicle sold_price for vehicles NOT already tracked via a deal (MED-6)
    const mtdRevenueVehicles = vehicles
      .filter(v => {
        if (v.status !== 'sold' || !v.sold_date || dealVehicleIds.has(v.id)) return false;
        const vDate = parseLocalDate(v.sold_date);
        return vDate ? (vDate.getMonth() === currentMonth && vDate.getFullYear() === currentYear) : false;
      })
      .reduce((sum, v) => sum + Number(v.sold_price || 0), 0);

    const mtdRevenue = mtdRevenueDeals + mtdRevenueVehicles;

    // Deals by stage
    const stageCount: Record<string, number> = {};
    allDeals.forEach(d => {
      stageCount[d.stage] = (stageCount[d.stage] || 0) + 1;
    });

    const overdue = ((tasksRes.data as any[]) || []).filter(t => isPast(new Date(t.due_date + "T00:00:00")));

    setStats({
      totalVehicles: vehiclesRes.count || 0,
      availableVehicles: available.length,
      soldVehicles: sold.length,
      totalLeads: leadsRes.count || 0,
      totalCustomers: customersRes.count || 0,
      hotLeads: customers.filter(c => c.lead_score === 'hot').length,
      avgDaysOnLot: avgDays,
      mtdRevenue,
    });
    setDeals(allDeals);
    setDealsByStage(stageCount);
    setRecentActivities((activitiesRes.data as any[]) || []);
    setOverdueTasks(overdue);
  };

  const activityTypeEmoji: Record<string, string> = {
    note: "📝", call: "📞", email: "📧", sms: "💬",
    meeting: "🤝", test_drive: "🚗", follow_up: "🔔",
    status_change: "🔄", system: "⚙️",
  };

  const inventoryStats = [
    { label: "Available", count: stats.availableVehicles, pct: stats.totalVehicles > 0 ? (stats.availableVehicles / stats.totalVehicles * 100) : 0, color: "bg-green-500" },
    { label: "Sold", count: stats.soldVehicles, pct: stats.totalVehicles > 0 ? (stats.soldVehicles / stats.totalVehicles * 100) : 0, color: "bg-blue-500" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start sm:items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your dealership</p>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" className="h-12 w-12 rounded-full shadow-lg fixed bottom-6 right-6 md:static md:h-10 md:w-auto md:rounded-md md:px-4 md:shadow-none z-50">
              <Plus className="h-6 w-6 md:h-4 md:w-4 md:mr-2" />
              <span className="hidden md:inline">Quick Add</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/admin/inventory?new=1')} className="cursor-pointer">
              <Car className="mr-2 h-4 w-4" />
              <span>New Vehicle</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="p-1 space-y-1">
              <AddCustomerDialog 
                onCreated={loadAll} 
                trigger={<Button variant="ghost" className="w-full justify-start font-normal h-8 px-2"><User className="mr-2 h-4 w-4" /> New Customer</Button>} 
              />
              <DropdownMenuItem onClick={() => navigate('/admin/deals?new=1')} className="cursor-pointer p-0">
                <Button variant="ghost" className="w-full justify-start font-normal h-8 px-2"><Handshake className="mr-2 h-4 w-4" /> New Deal</Button>
              </DropdownMenuItem>
              <CreateTaskDialog 
                onCreated={loadAll} 
                trigger={<Button variant="ghost" className="w-full justify-start font-normal h-8 px-2"><ListTodo className="mr-2 h-4 w-4" /> New Task</Button>} 
              />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/inventory')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Car className="h-3 w-3" /> Total Inventory</div>
            <div className="text-2xl font-bold">{stats.totalVehicles}</div>
            <div className="text-xs text-muted-foreground mt-1">{stats.availableVehicles} available</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/customers')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Users className="h-3 w-3" /> Customers</div>
            <div className="text-2xl font-bold">{stats.totalCustomers}</div>
            <div className="text-xs mt-1"><span className="text-red-500 font-semibold">{stats.hotLeads} hot</span> leads</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/deals')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><DollarSign className="h-3 w-3" /> MTD Revenue</div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(stats.mtdRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Clock className="h-3 w-3" /> Avg Days on Lot</div>
            <div className="text-2xl font-bold">{stats.avgDaysOnLot}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Overview */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Target className="h-4 w-4" /> Deal Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {DEAL_STAGES.map(stage => {
                const count = dealsByStage[stage.key] || 0;
                const maxCount = Math.max(...Object.values(dealsByStage), 1);
                return (
                  <div key={stage.key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span style={{ color: stage.color }} className="font-medium">{stage.label}</span>
                      <span className="font-semibold">{count} deals</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${(count / maxCount) * 100}%`, backgroundColor: stage.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Inventory Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Car className="h-4 w-4" /> Inventory Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {inventoryStats.map(s => (
                <div key={s.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">{s.label}</span>
                    <span className="font-semibold">{s.count} units</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full ${s.color}`} style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Leads Summary */}
            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold text-muted-foreground">Form Submissions</span>
                <span className="text-sm font-bold">{stats.totalLeads}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overdue Tasks */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" /> Overdue Tasks
              {overdueTasks.length > 0 && <Badge variant="destructive" className="text-xs">{overdueTasks.length}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overdueTasks.length > 0 ? (
              <div className="space-y-3">
                {overdueTasks.slice(0, 5).map(task => (
                  <div key={task.id} className="flex items-start gap-3 text-sm cursor-pointer hover:bg-muted/50 rounded-lg p-2 -mx-2" onClick={() => navigate('/admin/tasks')}>
                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{task.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {task.customer && `${task.customer.first_name} ${task.customer.last_name} · `}
                        Due {formatDistanceToNow(new Date(task.due_date + "T00:00:00"), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <span className="text-green-500">✓</span> All caught up!
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivities.length > 0 ? (
              <div className="space-y-3">
                {recentActivities.map(act => (
                  <div key={act.id} className="flex items-start gap-3 text-sm">
                    <span className="text-sm flex-shrink-0">{activityTypeEmoji[act.type] || '📋'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{act.subject}</div>
                      <div className="text-xs text-muted-foreground">
                        {act.customer && `${act.customer.first_name} ${act.customer.last_name} · `}
                        {formatDistanceToNow(new Date(act.created_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">No recent activity</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
